/**
 * studentActivityViewModel — l'état de l'exécution d'une activité (`engine/activity/useActivityRun`)
 * traduit en ce que `<StudentActivityPage>` et ses boîtes montrent. Pur.
 *
 * Ce que le moteur décide, la page le dit ; les mots sont ceux de l'ancien écran (`ActivityScreen`,
 * `ActivityWrapperScreen.onExit`, `ActivityDeletedModal`) :
 *  - la copie : chaque pièce, envoyée, en route (estompée) ou pas partie (cernée, un appui la renvoie) ;
 *    pour l'oral, l'enregistrement et sa durée ;
 *  - « J'ai fini » : il tourne pendant que la copie finit de partir puis que la finalisation part.
 *    Comme aujourd'hui, il n'est jamais grisé une fois la copie commencée (l'ancien bouton ne l'était
 *    que sans copie : la barre le cache alors) — une photo qui part encore, il l'attend ;
 *  - l'échec de « J'ai fini », visible avec « Réessayer » ;
 *  - la question de la sortie, avec ou sans copie ;
 *  - l'activité qui n'existe plus.
 */
import { type ActivityRun } from "../../../../../engine/activity/useActivityRun"
import { type UICopyPhoto, type UICopyRecording } from "../../../ui/types/studentActivity"

export type StudentActivityCopyModel = {
    medium: "photo" | "audio"
    photos: UICopyPhoto[]
    recording: UICopyRecording | null
    instruction?: string
    finishing: boolean
    finishDisabled: boolean
}

/** Le moteur, vu de la page : seulement ce qu'elle lit. */
export type ActivityRunView = Pick<ActivityRun, "medium" | "pieces" | "state" | "copyInFlight" | "stepDescriptions">

export function studentActivityCopy(run: ActivityRunView): StudentActivityCopyModel {
    const photos: UICopyPhoto[] =
        run.medium === "photo"
            ? run.pieces.map((piece) => ({ id: piece.key, uri: piece.uri, status: piece.status }))
            : []
    const last = run.medium === "audio" ? run.pieces[run.pieces.length - 1] : undefined
    return {
        medium: run.medium,
        photos,
        recording: last != null ? { durationSeconds: Math.round((last.durationMs ?? 0) / 1000) } : null,
        instruction: run.stepDescriptions[1] ?? undefined,
        finishing: run.state.phase === "finishing",
        finishDisabled: false,
    }
}

export type StudentActivityDialog = {
    title: string
    message?: string
    confirmLabel: string
    cancelLabel?: string
    /** La boîte n'a qu'un bouton : elle annonce. */
    single?: boolean
    /** L'ancienne boîte se fermait par sa croix : l'annulation se mesure comme elle. */
    cancelIsClose?: boolean
    danger: boolean
}

const NOT_SAVED: StudentActivityDialog = {
    title: "Ton travail n'est pas encore sauvegardé",
    message: "Vérifie ta connexion puis réessaie d'envoyer ton travail.",
    confirmLabel: "Réessayer",
    cancelLabel: "Annuler",
    cancelIsClose: true,
    danger: false,
}

const COPY_NOT_SENT: StudentActivityDialog = {
    ...NOT_SAVED,
    title: "Ton travail n'est pas encore parti",
    message: "Réessaie dans un instant.",
}

const COPY_STILL_NOT_SENT: StudentActivityDialog = {
    ...NOT_SAVED,
    title: "Ton travail ne part toujours pas",
    message: "Préviens ton masteur, puis réessaie dans un instant.",
}

/**
 * L'échec de « J'ai fini », avec « Réessayer ». En live, une copie qui ne part pas n'accuse pas la
 * connexion : le stockage peut être injoignable pendant que le live marche, sous les yeux de l'élève, et
 * le masteur est là pour être prévenu. Hors live, rien ne dit que la connexion marche, ni personne à
 * prévenir : les mots d'avant restent.
 */
export function finishFailureDialog(
    failure: ActivityRun["state"]["failure"],
    context: { copyFailures: number; live: boolean } = { copyFailures: 0, live: false }
): StudentActivityDialog | null {
    switch (failure) {
        case "copy_failed":
            if (!context.live) return NOT_SAVED
            return context.copyFailures > 1 ? COPY_STILL_NOT_SENT : COPY_NOT_SENT
        case "not_saved":
            return NOT_SAVED
        case "offline":
            return { ...NOT_SAVED, title: "Pas de connexion Internet" }
        case "server":
        case "rejected":
            return {
                title: "Erreur",
                message: "Une erreur s'est produite lors de l'envoi de ton travail",
                confirmLabel: "Réessayer",
                cancelLabel: "Annuler",
                danger: false,
            }
        default:
            return null
    }
}

/** La question du retour (`ActivityWrapperScreen.onExit`). */
export function exitDialog(question: ActivityRun["state"]["exitQuestion"]): StudentActivityDialog | null {
    if (question === "with_work") {
        return {
            title: "Avant de quitter, ton travail sera soumis",
            message: "Et c'est là que tu recevras tes XP !",
            confirmLabel: "Soumettre et quitter",
            cancelLabel: "Retour",
            danger: false,
        }
    }
    if (question === "without_work") {
        return {
            title: "Veux-tu quitter ?",
            message: "Les chronomètres seront sauvegardés, et tu pourras reprendre quand tu veux",
            confirmLabel: "Quitter",
            cancelLabel: "Retour",
            danger: false,
        }
    }
    return null
}

/** L'activité n'existe plus (`ActivityDeletedModal`). */
export const DELETED_DIALOG: StudentActivityDialog = {
    title: "Cette activité n'est plus disponible",
    message: "Tu vas passer à la suivante automatiquement.",
    confirmLabel: "Continuer",
    single: true,
    danger: false,
}

/** Une pièce enregistrée qu'on n'a pas pu retirer : on le dit, elle reste. */
export const DELETE_FAILED_DIALOG: StudentActivityDialog = {
    title: "Erreur",
    message: "La photo n'a pas pu être supprimée. Vérifie ta connexion puis réessaie.",
    confirmLabel: "OK",
    single: true,
    danger: false,
}
